import { TFunction } from "i18next";
import { DateType } from "@shared/contexts";
import { InvalidValueException } from "@shared/utils";
import { t_warningTexts } from "@i18n/usualTexts/t_warningTexts";

class DateService {
  static intPattern = /^\d+$/;

  static daysOfMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  static currentDate = new Date();

  static currentDay = DateService.currentDate.getDate();
  static currentMonth = DateService.currentDate.getMonth() + 1;
  static currentYear = DateService.currentDate.getFullYear();

  valueMonths: Array<string>;

  field: string;

  day: string;
  month: string;
  year: string;

  d: number;
  m: number;
  y: number;

  t: TFunction<"translation", undefined>

  constructor(day: string, month: string, year: string, field: string, valueMonths: Array<string>, t: TFunction<"translation", undefined>) {
    this.valueMonths = valueMonths;

    this.field = field;

    this.day = day;
    this.month = month;
    this.year = year;

    this.y = parseInt(year);
    this.m = valueMonths.indexOf(month) + 1;
    this.d = parseInt(day);

    this.t = t;
  }

  private isYearInvalid(): boolean {
    return this.y > DateService.currentYear || this.y < 1900 || !DateService.intPattern.test(this.year);
  }

  validateYear() {
    if (this.isYearInvalid()) {
      throw new InvalidValueException([this.field, this.t(t_warningTexts.date.year)]);
    }
  }

  validateMonth() {
    // When month is not found in valueMonths, indexOf returns -1, so m = -1 + 1 = 0
    if (this.m === 0) {
      throw new InvalidValueException([this.field, this.t(t_warningTexts.date.month)]);
    }
  }

  private static isLeapYear(year: number): boolean {
    return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
  }

  private static maxDayForMonth(month: number, year: number): number {
    if (month === 2 && DateService.isLeapYear(year)) return 29;
    return DateService.daysOfMonth[month];
  }

  validateDay() {
    if (!DateService.intPattern.test(this.day)) {
      throw new InvalidValueException([this.field, this.t(t_warningTexts.date.day)]);
    }

    if (this.m === 0) {
      return;
    }

    if (!(0 < this.d && this.d <= DateService.maxDayForMonth(this.m, this.y))) {
      throw new InvalidValueException([this.field, this.t(t_warningTexts.date.day)]);
    }
  }

  validateAge() {
    let age = DateService.currentYear - this.y;
    if (DateService.currentMonth <= this.m && DateService.currentDay < this.d) {
      age--;
    }

    if (age < 18) {
      throw new InvalidValueException([
        this.field,
        this.t(t_warningTexts.date.age),
      ]);
    }
  }
}

export default DateService;

function padZero(value: string): string {
  return value.length === 1 ? '0' + value : value;
}

export function dateFormat(object: DateType, valueMonths: Array<string>) {
  const month = (valueMonths.indexOf(object.month) + 1).toString();
  return `${object.year}-${padZero(month)}-${padZero(object.day)}`;
}